import {
  getRatingUomLabel,
  RATING_UOMS,
  useCargoDefinitions,
  type CargoDefinition,
  type CreateCustomerRateSheetRequest,
  type CreateCustomerRouteSheetRequest,
  type CustomerRateSheet,
  type CustomerRouteSheet,
  type LaneMatchModeValue,
  type RateBandInput,
  type RateSheetLineInput,
  type RatingUomValue,
  type RegionEndpoint,
  type RouteSheetLineInput,
  type UpdateCustomerRateSheetRequest,
  type UpdateCustomerRouteSheetRequest,
} from '@my/tms-api';
import { useLocations } from '@my/directory-api/hooks';
import { cn, ErrorBanner, Loading } from '@my/ui';
import { Check, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  LANE_MATCH_MODES,
  RATE_METHODS,
  RULE_TYPES,
  type RateMethod,
  type TransportRuleType,
} from './ratesChargesMock';
import { LaneEndpointPicker, type RouteEndpoint } from './LaneEndpointPicker';
import { Toggle } from './Toggle';

/**
 * What the form is editing, when it isn't adding. The two sheet kinds are
 * different shapes with different grids, so a sheet can't change kind — the
 * option cards lock while editing.
 */
export type TransportSheetEdit =
  | { kind: 'rateSheet'; sheet: CustomerRateSheet }
  | { kind: 'routeSheet'; sheet: CustomerRouteSheet };

export interface TransportChargeRuleFormProps {
  organisationId: string;
  customerPartyId: string;
  /** Party currency — ISO code for the request, symbol for the inputs. */
  currencyIsoCode: string;
  currencySymbol: string;
  /** When set, the form edits this sheet in place instead of adding one. */
  editing?: TransportSheetEdit | null;
  onCancel: () => void;
  /** Fired with the built rate-sheet create request. */
  onSaveEngine: (request: CreateCustomerRateSheetRequest) => void;
  /** Fired with the built route-sheet create request. */
  onSaveRoute: (request: CreateCustomerRouteSheetRequest) => void;
  /** Required when editing a rate sheet — emits the full-replace update. */
  onUpdateEngine?: (request: UpdateCustomerRateSheetRequest) => void;
  /** Required when editing a route sheet — emits the full-replace update. */
  onUpdateRoute?: (request: UpdateCustomerRouteSheetRequest) => void;
}

const numOrNull = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const numOrZero = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const toRegion = (e: RouteEndpoint): RegionEndpoint => ({
  locationId: e.locationId,
  name: e.label,
  country: e.countryIsoCode,
  city: e.city || null,
  postcode: e.postcode || null,
  placeId: e.placeId || null,
  latitude: e.latitude,
  longitude: e.longitude,
});

/**
 * Stored endpoint → picker value. Built from the sheet's own snapshot rather
 * than looked up in the Locations list, so editing a lane still works when the
 * location behind it has since been renamed or removed.
 */
const fromRegion = (e: RegionEndpoint): RouteEndpoint | null =>
  e.locationId
    ? {
        locationId: e.locationId,
        label: e.name || e.city || e.country,
        countryIsoCode: e.country,
        city: e.city ?? '',
        postcode: e.postcode ?? '',
        placeId: e.placeId ?? '',
        latitude: e.latitude ?? null,
        longitude: e.longitude ?? null,
      }
    : null;

/** Subject key a rate-sheet line's bands are stored under while editing. */
const subjectKeyOf = (line: CustomerRateSheet['lines'][number]): string =>
  line.subjectKind === 'Variant' ? (line.cargoVariantId ?? '') : `uom:${line.uom ?? ''}`;

/**
 * One band in a variant's rate ladder. `from` is NOT stored — it's derived
 * (first band = 1, each subsequent = previous band's `to` + 1). An empty
 * `to` marks the open-ended final band (∞).
 */
interface BandRow {
  to: string;
  rate: string;
}

const FIRST_BAND: BandRow[] = [{ to: '', rate: '' }];

/** One included cargo type on a route sheet: priced by a chosen UOM. */
interface RouteRowDraft {
  uom: RatingUomValue | null;
  charge: string;
  extraDrop: string;
}

const EMPTY_ROUTE_ROW: RouteRowDraft = { uom: null, charge: '', extraDrop: '' };

/** Min / max charges off an edited sheet, keyed by subject for the grid. */
function moneyBySubject(
  sheet: CustomerRateSheet | null,
  pick: (line: CustomerRateSheet['lines'][number]) => number | null | undefined,
): Record<string, string> {
  if (!sheet) return {};
  const out: Record<string, string> = {};
  for (const line of sheet.lines) {
    const v = pick(line);
    if (v != null) out[subjectKeyOf(line)] = String(v);
  }
  return out;
}

/** Parse a cargo definition's comma-joined `unitsOfMeasure` into UOM values. */
function parseUoms(def: CargoDefinition): RatingUomValue[] {
  return def.unitsOfMeasure
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is RatingUomValue => (RATING_UOMS as readonly string[]).includes(s));
}

/**
 * "Add charges" — the form behind the Transport Charges grid's + button.
 *
 * The option cards morph the form:
 *  - Rate sheet  → one cargo type + rate method + bands + min/max + dates.
 *  - Route sheet → one lane (two Locations) + the cargo types it covers,
 *                  each with a UOM, a fixed charge and a per-drop add-on.
 *
 * Cargo types come live from the org's cargo definitions, lane ends from its
 * Locations. A route sheet covers ONE lane: the same cargo type can be priced
 * on as many lanes as the customer has agreed — save again with a different
 * lane. What the BE rejects is a second route charge for the same cargo type
 * on the SAME lane over an overlapping period (or a cargo type priced by both
 * a rate sheet and a route line at once); that Conflict surfaces as a toast.
 */
export function TransportChargeRuleForm({
  organisationId,
  customerPartyId,
  currencyIsoCode,
  currencySymbol,
  editing,
  onCancel,
  onSaveEngine,
  onSaveRoute,
  onUpdateEngine,
  onUpdateRoute,
}: TransportChargeRuleFormProps) {
  const editRate = editing?.kind === 'rateSheet' ? editing.sheet : null;
  const editRoute = editing?.kind === 'routeSheet' ? editing.sheet : null;
  const cargoQuery = useCargoDefinitions(organisationId);
  // Lane ends come from the org's Locations — the same catalogue orders are
  // booked against, so a lane can be matched to a movement by id.
  const locationsQuery = useLocations(organisationId);

  const [ruleType, setRuleType] = useState<TransportRuleType>(
    editRoute ? 'routeSheet' : 'rateSheet',
  );
  // ── Rate sheet — ONE cargo type → its items (Pallet) or one UOM. ──────
  const [selectedDefId, setSelectedDefId] = useState<string | null>(
    editRate?.cargoDefinitionId ?? null,
  );
  const [selectedUom, setSelectedUom] = useState<RatingUomValue | null>(
    editRate?.lines.find((l) => l.subjectKind === 'Uom')?.uom ?? null,
  );
  const [method, setMethod] = useState<RateMethod>(editRate?.rateMethod ?? 'Band');
  // Rate bands per subject (item / UOM) — ladder of From/To/Rate rows. The
  // stored ladder ends open (no "To"); when it doesn't, a blank row is opened
  // so the operator can keep extending it.
  const [bandsByVariant, setBandsByVariant] = useState<Record<string, BandRow[]>>(() => {
    if (!editRate) return {};
    const out: Record<string, BandRow[]> = {};
    for (const line of editRate.lines) {
      const rows = [...line.bands]
        .sort((a, b) => a.sequence - b.sequence)
        .map((b) => ({ to: b.toQty != null ? String(b.toQty) : '', rate: String(b.rate) }));
      if (rows.length === 0 || rows[rows.length - 1]!.to !== '') rows.push({ to: '', rate: '' });
      out[subjectKeyOf(line)] = rows;
    }
    return out;
  });
  // Min / max charge per subject (first band row).
  const [minByVariant, setMinByVariant] = useState<Record<string, string>>(() =>
    moneyBySubject(editRate, (l) => l.minCharge),
  );
  const [maxByVariant, setMaxByVariant] = useState<Record<string, string>>(() =>
    moneyBySubject(editRate, (l) => l.maxCharge),
  );

  // ── Route sheet — ONE lane → included cargo types, each priced by a UOM.
  const [routeOrigin, setRouteOrigin] = useState<RouteEndpoint | null>(
    editRoute ? fromRegion(editRoute.origin) : null,
  );
  const [routeDestination, setRouteDestination] = useState<RouteEndpoint | null>(
    editRoute ? fromRegion(editRoute.destination) : null,
  );
  const [matchMode, setMatchMode] = useState<LaneMatchModeValue>(editRoute?.matchMode ?? 'Exact');
  const [includedDefIds, setIncludedDefIds] = useState<Set<string>>(
    () => new Set((editRoute?.lines ?? []).map((l) => l.cargoDefinitionId)),
  );
  const [routeRows, setRouteRows] = useState<Record<string, RouteRowDraft>>(() => {
    const out: Record<string, RouteRowDraft> = {};
    for (const l of editRoute?.lines ?? []) {
      out[l.cargoDefinitionId] = {
        uom: l.uom,
        charge: String(l.charge),
        extraDrop: l.extraDrop != null ? String(l.extraDrop) : '',
      };
    }
    return out;
  });

  // Both sheet kinds carry it, so it lives alongside the effective window
  // rather than inside either branch. New sheets charge without asking.
  const [autoApply, setAutoApply] = useState(editRate?.autoApply ?? editRoute?.autoApply ?? true);

  const [effectiveFrom, setEffectiveFrom] = useState(
    editRate?.effectiveFrom ?? editRoute?.effectiveFrom ?? '',
  );
  const [effectiveTo, setEffectiveTo] = useState(
    editRate?.effectiveTo ?? editRoute?.effectiveTo ?? '',
  );
  const [noExpiry, setNoExpiry] = useState(
    editing ? !(editRate?.effectiveTo ?? editRoute?.effectiveTo) : false,
  );

  const isEngine = ruleType === 'rateSheet';

  const defs = useMemo(
    () => (cargoQuery.data ?? []).filter((d) => d.status === 'Active'),
    [cargoQuery.data],
  );
  const locations = useMemo(
    () =>
      (locationsQuery.data ?? [])
        .filter((a) => a.status === 'Active')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [locationsQuery.data],
  );
  // The one cargo type a Rate sheet is built for.
  const selectedDef = useMemo(
    () => defs.find((d) => d.id === selectedDefId) ?? null,
    [defs, selectedDefId],
  );
  const isPalletDef = selectedDef?.cargoType === 'Pallet';

  // Non-pallet types are rated by a unit of measure — the definition's UOMs.
  const uoms = useMemo<RatingUomValue[]>(
    () => (selectedDef && !isPalletDef ? parseUoms(selectedDef) : []),
    [selectedDef, isPalletDef],
  );
  // Effective UOM = the picked one if still valid, else the first available.
  const effectiveUom = useMemo<RatingUomValue | null>(() => {
    if (uoms.length === 0) return null;
    return selectedUom && uoms.includes(selectedUom) ? selectedUom : (uoms[0] ?? null);
  }, [uoms, selectedUom]);

  // Grid subjects (rows): Pallet → each item (variant); otherwise → the
  // single chosen UOM. Bands / min / max are keyed by `subject.key`.
  const subjects = useMemo<{ key: string; label: string }[]>(() => {
    if (!selectedDef) return [];
    if (isPalletDef) return selectedDef.variants.map((v) => ({ key: v.id, label: v.name }));
    return effectiveUom
      ? [{ key: `uom:${effectiveUom}`, label: getRatingUomLabel(effectiveUom) }]
      : [];
  }, [selectedDef, isPalletDef, effectiveUom]);

  // Route sheet helpers — included cargo types + their per-type UOM/charge.
  const includedDefs = useMemo(
    () => defs.filter((d) => includedDefIds.has(d.id)),
    [defs, includedDefIds],
  );
  const toggleIncluded = (id: string) =>
    setIncludedDefIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const routeRowFor = (id: string): RouteRowDraft => routeRows[id] ?? EMPTY_ROUTE_ROW;
  const patchRouteRow = (id: string, patch: Partial<RouteRowDraft>) =>
    setRouteRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? EMPTY_ROUTE_ROW), ...patch } }));
  // Effective UOM for a route row: picked one if still valid, else first.
  const routeRowUom = (def: CargoDefinition): RatingUomValue | null => {
    const opts = parseUoms(def);
    const cur = routeRowFor(def.id).uom;
    return cur && opts.includes(cur) ? cur : (opts[0] ?? null);
  };

  const rowsFor = (variantId: string): BandRow[] => bandsByVariant[variantId] ?? FIRST_BAND;

  const patchBand = (variantId: string, i: number, patch: Partial<BandRow>) =>
    setBandsByVariant((prev) => {
      const rows = (prev[variantId] ?? FIRST_BAND).map((r, idx) =>
        idx === i ? { ...r, ...patch } : r,
      );
      // Filling the last band's "To" opens the next band automatically.
      if (i === rows.length - 1 && patch.to !== undefined && patch.to.trim() !== '') {
        rows.push({ to: '', rate: '' });
      }
      return { ...prev, [variantId]: rows };
    });

  const removeBand = (variantId: string, i: number) =>
    setBandsByVariant((prev) => {
      const rows = (prev[variantId] ?? FIRST_BAND).filter((_, idx) => idx !== i);
      return { ...prev, [variantId]: rows.length ? rows : [{ to: '', rate: '' }] };
    });

  const effectiveToOut = noExpiry ? null : effectiveTo || null;

  // A band row is only sent if something was typed into it — an untouched
  // trailing row is the ladder's "add next band" affordance, not a £0 band.
  const filledBands = (key: string) =>
    rowsFor(key).filter((r) => r.to.trim() !== '' || r.rate.trim() !== '');

  const canSave = isEngine
    ? selectedDefId != null &&
      // At least one subject priced. The rest are left out of the payload —
      // a pallet sheet needn't price every item the cargo type can carry.
      subjects.some((s) => filledBands(s.key).length > 0) &&
      !!effectiveFrom
    : routeOrigin != null &&
      routeDestination != null &&
      routeOrigin.locationId !== routeDestination.locationId &&
      !!effectiveFrom &&
      includedDefs.some((d) => routeRowFor(d.id).charge.trim() !== '');

  const handleSave = () => {
    if (!canSave) return;
    if (isEngine) {
      const def = selectedDef;
      if (!def) return;
      const lines: RateSheetLineInput[] = subjects
        .filter((s) => filledBands(s.key).length > 0)
        .map((s) => {
          const rows = filledBands(s.key);
          const bands: RateBandInput[] = [];
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i]!;
            const prev = bands[i - 1];
            // From is derived: 1 for the first band, else previous "To" + 1.
            const fromQty =
              i === 0 ? 1 : prev?.toQty != null ? prev.toQty + 1 : (prev?.fromQty ?? 1);
            bands.push({ fromQty, toQty: numOrNull(r.to), rate: numOrZero(r.rate), sequence: i });
          }
          return {
            subjectKind: isPalletDef ? 'Variant' : 'Uom',
            cargoVariantId: isPalletDef ? s.key : null,
            uom: isPalletDef ? null : effectiveUom,
            minCharge: numOrNull(minByVariant[s.key] ?? ''),
            maxCharge: numOrNull(maxByVariant[s.key] ?? ''),
            bands,
          };
        });
      if (editRate && onUpdateEngine) {
        onUpdateEngine({
          id: editRate.id,
          cargoDefinitionId: def.id,
          rateMethod: method,
          autoApply,
          effectiveFrom,
          effectiveTo: effectiveToOut,
          lines,
        });
        return;
      }
      onSaveEngine({
        organisationId,
        customerPartyId,
        cargoDefinitionId: def.id,
        rateMethod: method,
        currencyIsoCode,
        autoApply,
        effectiveFrom,
        effectiveTo: effectiveToOut,
        lines,
      });
      return;
    }
    // Route: one shared lane → a line per included cargo type with a charge.
    const lines: RouteSheetLineInput[] = includedDefs
      .map((def) => ({ def, row: routeRowFor(def.id), uom: routeRowUom(def) }))
      .filter(({ row, uom }) => row.charge.trim() !== '' && uom != null)
      .map(({ def, row, uom }) => ({
        cargoDefinitionId: def.id,
        uom: uom!,
        charge: numOrZero(row.charge),
        extraDrop: numOrNull(row.extraDrop),
      }));
    if (editRoute && onUpdateRoute) {
      onUpdateRoute({
        id: editRoute.id,
        autoApply,
        effectiveFrom,
        effectiveTo: effectiveToOut,
        matchMode,
        origin: toRegion(routeOrigin!),
        destination: toRegion(routeDestination!),
        lines,
      });
      return;
    }
    onSaveRoute({
      organisationId,
      customerPartyId,
      currencyIsoCode,
      autoApply,
      effectiveFrom,
      effectiveTo: effectiveToOut,
      matchMode,
      origin: toRegion(routeOrigin!),
      destination: toRegion(routeDestination!),
      lines,
    });
  };

  return (
    <div className="rounded-md border border-blue-200 bg-white">
      <div className="border-b border-blue-100 bg-blue-50/70 px-3 py-2 text-sm font-semibold text-blue-700">
        {editRate ? 'Edit rate sheet' : editRoute ? 'Edit route sheet' : 'Add charges'}
      </div>

      <div className="space-y-4 p-3">
        {/* What would you like to add? Locked on edit — the two sheet kinds
            are different shapes, so a sheet can't become the other one. */}
        {!editing && (
          <Field label="What would you like to add?">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {RULE_TYPES.map((r) => (
                <SelectCard
                  key={r.value}
                  active={ruleType === r.value}
                  title={r.label}
                  hint={r.hint}
                  onClick={() => setRuleType(r.value)}
                />
              ))}
            </div>
          </Field>
        )}

        {/* Rate method — sits right under Rule type for rate-engine rules. */}
        {isEngine && (
          <Field label="Rate method">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {RATE_METHODS.map((m) => (
                <SelectCard
                  key={m.value}
                  active={method === m.value}
                  title={m.label}
                  hint={m.hint}
                  onClick={() => setMethod(m.value)}
                />
              ))}
            </div>
          </Field>
        )}

        {/* Rate sheet: pick ONE cargo type — its items (Pallet) or chosen
            UOM become the grid subjects. */}
        {isEngine && (
          <Field
            label="Select cargo type"
            right={
              isPalletDef && selectedDef ? (
                <span className="text-[11px] font-medium text-blue-600">
                  {selectedDef.variants.length}{' '}
                  {selectedDef.variants.length === 1 ? 'item' : 'items'}
                </span>
              ) : null
            }
          >
            {cargoQuery.isPending && <Loading label="Loading cargo types" />}
            {cargoQuery.isError && (
              <ErrorBanner title="Failed to load cargo types" message={cargoQuery.error.message} />
            )}
            {cargoQuery.isSuccess &&
              (defs.length === 0 ? (
                <p className="rounded-md border border-slate-200 px-3 py-3 text-xs italic text-slate-400">
                  No cargo definitions configured for this organisation.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {defs.map((def) => (
                    <button
                      key={def.id}
                      type="button"
                      onClick={() => setSelectedDefId(def.id)}
                      className={cn(
                        'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors',
                        selectedDefId === def.id
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
                      )}
                    >
                      {def.displayName}
                    </button>
                  ))}
                </div>
              ))}
          </Field>
        )}

        {/* Unit of measure — non-Pallet cargo types on the rate sheet. */}
        {isEngine && selectedDef && !isPalletDef && (
          <Field label="Unit of measure">
            {uoms.length === 0 ? (
              <p className="text-xs italic text-slate-400">
                This cargo type has no units of measure configured.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {uoms.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setSelectedUom(u)}
                    className={cn(
                      'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors',
                      effectiveUom === u
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
                    )}
                  >
                    {getRatingUomLabel(u)}
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}

        {/* Rate sheet — one grid; the subject is each item (Pallet) or the
            chosen UOM (otherwise), each with a band ladder. Method is the
            single global selector above; Min/Max are per row (first band). */}
        {isEngine && (
          <Field label="Rate sheet">
            {!selectedDef ? (
              <p className="rounded-md border border-dashed border-slate-300 px-3 py-3 text-xs italic text-slate-400">
                Select a cargo type above to set its rates.
              </p>
            ) : subjects.length === 0 ? (
              <p className="rounded-md border border-dashed border-slate-300 px-3 py-3 text-xs italic text-slate-400">
                {isPalletDef
                  ? 'This cargo type has no items to rate.'
                  : 'Select a unit of measure above.'}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">Cargo type</th>
                      <th className="px-2 py-1.5 text-left font-medium">From</th>
                      <th className="px-2 py-1.5 text-left font-medium">To</th>
                      <th className="px-2 py-1.5 text-left font-medium">Rate / flat charge</th>
                      <th className="px-2 py-1.5 text-left font-medium">Min charge</th>
                      <th className="px-2 py-1.5 text-left font-medium">Max charge</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {subjects.map((s) => {
                      const variantId = s.key;
                      const name = s.label;
                      const rows = rowsFor(variantId);
                      return rows.map((b, i) => {
                        const prev = i > 0 ? rows[i - 1] : undefined;
                        // From is derived: 1 for the first band, else prev "To" + 1.
                        const from =
                          i === 0
                            ? '1'
                            : prev && prev.to.trim() !== '' && Number.isFinite(Number(prev.to))
                              ? String(Number(prev.to) + 1)
                              : '—';
                        const isOpenBand = b.to.trim() === '';
                        const first = i === 0;
                        return (
                          <tr
                            key={`${variantId}-${i}`}
                            className={cn(
                              'border-t',
                              first ? 'border-slate-200' : 'border-slate-100',
                            )}
                          >
                            <td className="px-3 py-1 align-middle font-semibold text-slate-900">
                              {first ? name : ''}
                            </td>
                            <td className="px-2 py-1 tabular-nums text-slate-500">{from}</td>
                            <td className="px-1 py-1">
                              <CellInput
                                value={b.to}
                                placeholder={isOpenBand ? '∞' : 'To'}
                                onChange={(v) => patchBand(variantId, i, { to: v })}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <CellInput
                                value={b.rate}
                                placeholder={`${currencySymbol}0.00`}
                                onChange={(v) => patchBand(variantId, i, { rate: v })}
                              />
                            </td>
                            <td className="px-1 py-1">
                              {first ? (
                                <CellInput
                                  value={minByVariant[variantId] ?? ''}
                                  placeholder={`${currencySymbol}0.00`}
                                  onChange={(v) =>
                                    setMinByVariant((p) => ({ ...p, [variantId]: v }))
                                  }
                                />
                              ) : null}
                            </td>
                            <td className="px-1 py-1">
                              {first ? (
                                <CellInput
                                  value={maxByVariant[variantId] ?? ''}
                                  placeholder="None"
                                  onChange={(v) =>
                                    setMaxByVariant((p) => ({ ...p, [variantId]: v }))
                                  }
                                />
                              ) : null}
                            </td>
                            <td className="px-1 py-1 text-right">
                              <button
                                type="button"
                                aria-label="Remove band"
                                onClick={() => removeBand(variantId, i)}
                                disabled={rows.length === 1}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-30"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Field>
        )}

        {/* Route sheet — one lane, then the cargo types it covers (each
            priced by a chosen UOM). */}
        {!isEngine && (
          <>
            <Field
              label="Route"
              right={
                <span className="text-[11px] text-slate-400">
                  One lane per route sheet — add another for the same cargo on a different lane.
                </span>
              }
            >
              {locationsQuery.isPending && <Loading label="Loading locations" />}
              {locationsQuery.isError && (
                <ErrorBanner
                  title="Failed to load locations"
                  message={locationsQuery.error.message}
                />
              )}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <p className="mb-0.5 text-[11px] font-medium text-slate-500">From</p>
                  <LaneEndpointPicker
                    value={routeOrigin}
                    options={locations}
                    excludeLocationId={routeDestination?.locationId ?? null}
                    placeholder="Search locations…"
                    onChange={setRouteOrigin}
                  />
                </div>
                <div>
                  <p className="mb-0.5 text-[11px] font-medium text-slate-500">To</p>
                  <LaneEndpointPicker
                    value={routeDestination}
                    options={locations}
                    excludeLocationId={routeOrigin?.locationId ?? null}
                    placeholder="Search locations…"
                    onChange={setRouteDestination}
                  />
                </div>
              </div>
            </Field>

            <Field label="How this lane matches a job">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {LANE_MATCH_MODES.map((m) => (
                  <SelectCard
                    key={m.value}
                    active={matchMode === m.value}
                    title={m.label}
                    hint={m.hint}
                    onClick={() => setMatchMode(m.value)}
                  />
                ))}
              </div>
            </Field>

            <Field
              label="Include cargo types"
              right={
                includedDefIds.size > 0 ? (
                  <span className="text-[11px] font-medium text-blue-600">
                    {includedDefIds.size} selected
                  </span>
                ) : null
              }
            >
              {cargoQuery.isPending && <Loading label="Loading cargo types" />}
              {cargoQuery.isError && (
                <ErrorBanner
                  title="Failed to load cargo types"
                  message={cargoQuery.error.message}
                />
              )}
              {cargoQuery.isSuccess &&
                (defs.length === 0 ? (
                  <p className="rounded-md border border-slate-200 px-3 py-3 text-xs italic text-slate-400">
                    No cargo definitions configured for this organisation.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {defs.map((def) => (
                      <button
                        key={def.id}
                        type="button"
                        onClick={() => toggleIncluded(def.id)}
                        className={cn(
                          'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors',
                          includedDefIds.has(def.id)
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400',
                        )}
                      >
                        {def.displayName}
                      </button>
                    ))}
                  </div>
                ))}
            </Field>

            <Field label="Route sheet">
              {includedDefs.length === 0 ? (
                <p className="rounded-md border border-dashed border-slate-300 px-3 py-3 text-xs italic text-slate-400">
                  Include cargo types above to price this route.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-slate-200">
                  <table className="w-full table-auto text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                      <tr>
                        <th className="px-3 py-1.5 text-left font-medium">Cargo type</th>
                        <th className="px-2 py-1.5 text-left font-medium">Rate by (UOM)</th>
                        <th className="px-2 py-1.5 text-left font-medium">Charge</th>
                        <th className="px-2 py-1.5 text-left font-medium">Extra drop</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {includedDefs.map((def) => {
                        const row = routeRowFor(def.id);
                        const opts = parseUoms(def);
                        const uom = routeRowUom(def);
                        return (
                          <tr key={def.id} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 align-middle font-semibold text-slate-900">
                              {def.displayName}
                            </td>
                            <td className="px-1 py-1.5">
                              <select
                                value={uom ?? ''}
                                onChange={(e) =>
                                  patchRouteRow(def.id, { uom: e.target.value as RatingUomValue })
                                }
                                disabled={opts.length === 0}
                                className="w-full rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              >
                                {opts.length === 0 && <option value="">—</option>}
                                {opts.map((u) => (
                                  <option key={u} value={u}>
                                    {getRatingUomLabel(u)}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1 py-1.5">
                              <CellInput
                                value={row.charge}
                                placeholder={`${currencySymbol}0.00`}
                                onChange={(v) => patchRouteRow(def.id, { charge: v })}
                              />
                            </td>
                            <td className="px-1 py-1.5">
                              <CellInput
                                value={row.extraDrop}
                                placeholder={`${currencySymbol}0.00 / drop`}
                                onChange={(v) => patchRouteRow(def.id, { extraDrop: v })}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Field>
          </>
        )}

        {/* Charging — the same question whichever sheet this is. */}
        <Field label="Charging">
          <div className="flex flex-wrap items-center gap-3">
            <Toggle checked={autoApply} onChange={setAutoApply} label="Charge automatically" />
            <span className="text-[11px] text-slate-400">
              {autoApply
                ? ruleType === 'routeSheet'
                  ? 'A matching route would be charged at this rate without asking.'
                  : 'A matching order would be charged at this rate without asking.'
                : ruleType === 'routeSheet'
                  ? 'Adding a matching route asks before the charge goes on.'
                  : 'Adding a matching order asks before the charge goes on.'}
            </span>
          </div>
        </Field>

        {/* Effective dates */}
        <Field label="Effective dates">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[11px] font-medium text-slate-500">
              From *
              <input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                className="mt-0.5 block w-44 rounded-sm border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </label>
            <label
              className={cn('text-[11px] font-medium text-slate-500', noExpiry && 'opacity-40')}
            >
              To
              <input
                type="date"
                value={effectiveTo}
                disabled={noExpiry}
                onChange={(e) => setEffectiveTo(e.target.value)}
                className="mt-0.5 block w-44 rounded-sm border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-slate-50"
              />
            </label>
            <Toggle checked={noExpiry} onChange={setNoExpiry} label="No expiry" />
          </div>
        </Field>

        {/* Actions */}
        <div className="flex items-center justify-end gap-1 border-t border-slate-200 pt-3">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────────────────────

function Field({
  label,
  right,
  children,
}: {
  label: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

function SelectCard({
  active,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2 text-left transition-colors',
        active ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      <div className={cn('text-xs font-semibold', active ? 'text-blue-700' : 'text-slate-700')}>
        {title}
      </div>
      <div className="text-[11px] text-slate-400">{hint}</div>
    </button>
  );
}

/** Subtle in-grid input — reads as plain text, shows a border on hover/focus. */
function CellInput({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-sm border border-transparent bg-transparent px-2 py-1 text-xs tabular-nums text-slate-700 placeholder:text-slate-400 hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  );
}
